import { useState } from "react";
import { Typography, Box, Button, TextField, InputAdornment, Grid, Checkbox, FormControlLabel } from "@mui/material";
import { CaseWhenBenefit, useSubmitReimbursementRequestMutation, WhenBenefitsDocument } from "services/GraphQl";
import moment from "moment";
import { useAuth } from "contexts/User";
import { useNotifications } from "contexts/Notification";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import { useForm, Controller } from "react-hook-form";
import { validateReimbursementRequest } from "../utils";
import { useFileHandling } from "../hooks";
import ReimbursementFileSection from "./ReimbursementFileSection";
import SubmitReimbursementHeader from "./shared/SubmitReimbursementHeader";
import ReimbursementConfirmation from "./shared/ReimbursementConfirmation";

interface ReimbursementFormData {
  amount: number;
  confirmation: boolean;
  understanding: boolean;
  coverageMonths: string[];
}

function SubmitMonthlyReimbursementRequest({
  hasBankAccount,
  whenBenefit,
  companyId,
  caseId,
  onSave,
  onCancel,
}: {
  hasBankAccount: boolean;
  whenBenefit: CaseWhenBenefit;
  companyId: string;
  caseId: string;
  onSave: Function;
  onCancel: Function;
}) {
  const { user } = useAuth();
  const [submitReimbursementRequest] = useSubmitReimbursementRequestMutation({
    refetchQueries: [WhenBenefitsDocument],
    awaitRefetchQueries: true,
  });
  const { notifications } = useNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    files,
    fileError,
    uploading,
    uploadFile,
    photo,
    setPhoto,
    facingMode,
    setFacingMode,
    webcamRef,
    takePhoto,
    uploadFiles,
    deleteFile,
  } = useFileHandling();

  const amountRemaining = whenBenefit.detail?.amountRemaining || 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ReimbursementFormData & { coverageMonths: string[] }>({
    defaultValues: {
      amount: "" as any,
      confirmation: false,
      understanding: false,
      coverageMonths: [],
    },
  });

  const onSubmit = async (data: ReimbursementFormData) => {
    if (isSubmitting || !user) return;
    if (!validateReimbursementRequest(data, files, amountRemaining, hasBankAccount, notifications)) {
      return;
    }
    try {
      setIsSubmitting(true);
      await submitReimbursementRequest({
        variables: {
          whenBenefitId: (whenBenefit as any).id || "",
          files,
          amount: data.amount,
          months: data.coverageMonths,
          userId: user.uid,
          companyId,
          caseId,
        },
      });
      notifications.success(
        `Reimbursement request of ${data.amount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })} submitted successfully.`
      );
      onSave();
    } catch (error) {
      console.error("Error submitting reimbursement request:", error);
      notifications.error("An error occurred while submitting the request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMonths = watch("coverageMonths");
  const amount = watch("amount");

  // Calculate allocation summary
  const allocationCount = selectedMonths.length;
  const perMonth = allocationCount > 0 ? amount / allocationCount : 0;
  const selectedMonthObjs = whenBenefit.months?.filter((m) => selectedMonths.includes(m.month)) || [];
  const overLimit = selectedMonthObjs.some(
    (m) => perMonth > (m.amount ?? 0) - (m.reimbursedAmount ?? 0) - (m.requested ?? 0)
  );

  if (!whenBenefit) {
    return null;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 4,
      }}
    >
      <SubmitReimbursementHeader />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: "Amount is required",
              min: { value: 0.01, message: "Amount must be greater than 0" },
              max: { value: amountRemaining, message: "Amount cannot exceed remaining benefit" },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Amount Requested"
                data-testid="amount-requested"
                variant="outlined"
                fullWidth
                type="number"
                error={!!errors.amount}
                helperText={errors.amount?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: {
                    step: "0.01",
                    pattern: "\\d*\\.?\\d{0,2}",
                  },
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === null) {
                    field.onChange("");
                  } else {
                    const numValue = parseFloat(value);
                    field.onChange(isNaN(numValue) ? "" : numValue);
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body1"
            sx={{
              pb: { xs: 0, sm: 2 },
              paddingLeft: { xs: 0, sm: 2 },
              paddingTop: { xs: 1, sm: 0 },
            }}
          >
            Remaining Benefit: <FormattedCurrency value={amountRemaining} />
          </Typography>
        </Grid>
      </Grid>

      {/* Coverage Months Section */}
      <Box>
        <Typography variant="h3" sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem" }, mt: 2 }}>
          Coverage Months
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Please select the months you want to apply this reimbursement to. The requested amount will be divided equally
          between selected months.
        </Typography>
        <Controller
          name="coverageMonths"
          control={control}
          rules={{
            validate: (value) => (value && value.length > 0) || "At least one month must be selected",
          }}
          render={({ field }) => (
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 1,
                maxHeight: 400,
                overflowY: "auto",
                position: "relative",
              }}
            >
              {/* Static header above scrollable months list */}
              <Grid
                container
                wrap="nowrap"
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #ddd",
                  backgroundColor: "white",
                  zIndex: 2,
                  height: "50px",
                  alignItems: "center",
                }}
              >
                <Grid item xs={5} sx={{ whiteSpace: "nowrap" }}>
                  Month
                </Grid>
                <Grid item xs={3} sx={{ whiteSpace: "nowrap" }}>
                  Amount Remaining
                </Grid>
                <Grid item xs={3}></Grid>
                <Grid item xs={1}></Grid>
              </Grid>
              <Box
                sx={{
                  border: "1px solid #ddd",
                  p: 1,
                  maxHeight: 200,
                  overflowY: "auto",
                  position: "relative",
                  mt: 0,
                }}
              >
                {whenBenefit.months
                  ?.map((monthObj) => {
                    const remaining =
                      (monthObj.amount ?? 0) - (monthObj.reimbursedAmount ?? 0) - (monthObj.requested ?? 0);

                    const isActive = !monthObj.deadline || !moment().isAfter(moment(monthObj.deadline), 'day');
                    return (
                      <Grid container alignItems="center" key={monthObj.month} sx={{ mb: 0.5 }}>
                        <Grid item xs={5}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value.includes(monthObj.month)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    field.onChange([...field.value, monthObj.month]);
                                  } else {
                                    field.onChange(field.value.filter((m) => m !== monthObj.month));
                                  }
                                }}
                                disabled={!amount || amount <= 0 || !isActive}
                              />
                            }
                            label={
                              <span>
                                {moment(monthObj.month, "YYYY-MM").format("MMMM YYYY")}
                                <br />
                                <span
                                  style={{
                                    color: (monthObj.requested ?? 0) > 0 ? "#b8860b" : "#b0b0b0",
                                    fontSize: "0.95em",
                                    fontWeight: 400,
                                  }}
                                >
                                  (
                                  {(monthObj.requested ?? 0).toLocaleString("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  })}{" "}
                                  pending)
                                </span>
                              </span>
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <span style={{ color: remaining <= 0 ? "red" : "green" }}>${remaining.toFixed(2)}</span>
                        </Grid>
                        <Grid item xs={3}>
                          {isActive ? (
                            <span style={{ color: "green" }}>Active</span>
                          ) : (
                            <span style={{ color: "red" }}>Expired</span>
                          )}
                        </Grid>
                        <Grid item xs={1}></Grid>
                      </Grid>
                    );
                  })}
              </Box>
            </Box>
          )}
        />
        {errors.coverageMonths && (
          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
            {errors.coverageMonths.message}
          </Typography>
        )}
      </Box>

      {/* Allocation Summary Section */}
      <Box
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: overLimit ? "error.main" : "success.main",
          borderRadius: 2,
          p: 2,
          background: overLimit ? "#fff6f6" : "#f6fff6",
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem" }, color: overLimit ? "error.main" : "success.main", mb: 1 }}
        >
          Allocation Summary
        </Typography>
        {allocationCount === 0 ? (
          <Typography variant="body2">Select at least one month to see allocation details.</Typography>
        ) : (
          <>
            <Typography variant="body2">
              Amount of {amount.toLocaleString("en-US", { style: "currency", currency: "USD" })} across{" "}
              {allocationCount} month{allocationCount > 1 ? "s" : ""}.
            </Typography>
            <Typography variant="body2">
              Per month: {perMonth.toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {selectedMonthObjs.map((m) => {
                const remaining = (m.amount ?? 0) - (m.reimbursedAmount ?? 0) - (m.requested ?? 0);
                const exceeds = perMonth > remaining;
                return (
                  <Box key={m.month} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 120 }}>
                      {moment(m.month, "YYYY-MM").format("MMMM YYYY")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: exceeds ? "error.main" : "success.main", fontWeight: exceeds ? 700 : 400 }}
                    >
                      {perMonth.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                      {exceeds && <span style={{ marginLeft: 8 }}>• Exceeds limit</span>}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {overLimit && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                The requested amount cannot be distributed evenly across the selected months
              </Typography>
            )}
          </>
        )}
      </Box>

      <ReimbursementFileSection
        files={files}
        fileError={fileError}
        uploading={uploading}
        uploadFile={uploadFile}
        photo={photo}
        setPhoto={setPhoto}
        facingMode={facingMode}
        setFacingMode={setFacingMode}
        webcamRef={webcamRef}
        takePhoto={takePhoto}
        uploadFiles={uploadFiles}
        deleteFile={deleteFile}
      />

      <ReimbursementConfirmation control={control} errors={errors} hasBankAccount={hasBankAccount} />

      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
        <Button
          type="submit"
          sx={{ fontSize: { xs: "0.675rem", sm: "0.875rem" } }}
          variant="contained"
          color="secondary"
          data-testid="submitReimbursementButton"
          disabled={isSubmitting}
        >
          Submit Reimbursement Request
        </Button>
        <Button
          sx={{ fontSize: { xs: "0.675rem", sm: "0.875rem" } }}
          variant="contained"
          color="secondary"
          onClick={() => onCancel()}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

export default SubmitMonthlyReimbursementRequest;
